import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser, updateUser, logout } from '../../store/slices/authSlice';
import { useGetWhatsappPhonesQuery, useGetCurrentUserQuery } from '../../store/services/whatsappApi';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = true, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { data: currentUser, isLoading: isUserLoading } = useGetCurrentUserQuery();
  const { data: phones, isLoading: isPhonesLoading } = useGetWhatsappPhonesQuery();

  // Keep auth store in sync with current user API
  useEffect(() => {
    if (currentUser) {
      dispatch(updateUser({
        id: currentUser.id,
        email: currentUser.email,
        name: currentUser.name,
        role: currentUser.role || 'user',
      } as any));
      try {
        localStorage.setItem('user', JSON.stringify(currentUser));
      } catch {}
    }
  }, [currentUser, dispatch]);

  // Handle logout
  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };
  return (
    <div className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out md:translate-x-0 z-20`}>
      {/* Sidebar header with logo */}
      <div className="h-16 bg-whatsapp-teal flex items-center justify-center">
        <img src="/logo/favicon-32x32.png" alt="Logo" className="h-8 w-8 mr-2" />
        <div className="text-white text-xl font-bold">WhatsApp Business</div>
        {/* Close button for mobile */}
        <button 
          onClick={onClose}
          className="absolute right-2 top-3 md:hidden text-white hover:text-gray-300 focus:outline-none"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* User profile section */}
      <div className="p-4 border-b border-gray-200">
        {isUserLoading ? (
          <div className="flex items-center animate-pulse">
            <div className="h-10 w-10 rounded-full bg-gray-200" />
            <div className="ml-3">
              <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-2 w-40 bg-gray-200 rounded" />
            </div>
          </div>
        ) : (
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-whatsapp-green flex items-center justify-center text-white">
              <span className="text-lg font-semibold">{(currentUser?.name || user?.name || 'U').charAt(0).toUpperCase()}</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">{currentUser?.name || user?.name || 'User'}</p>
              <p className="text-xs font-medium text-gray-500">{currentUser?.email || user?.email || 'user@example.com'}</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <nav className="mt-4 px-2">
        <div className="space-y-1">
          {/* Dashboard */}
          <Link 
            to="/dashboard" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname === '/' || location.pathname === '/dashboard' ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname === '/' || location.pathname === '/dashboard' ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
            </svg>
            Dashboard
          </Link>
          
          {/* Messages */}
          <Link 
            to="/messages" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname.includes('/messages') ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname.includes('/messages') ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
            </svg>
            Messages
          </Link>
          
          {/* Contacts */}
          <Link 
            to="/contacts" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname === '/contacts' ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname === '/contacts' ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
            Contacts
          </Link>
          
          {/* Templates */}
          <Link 
            to="/templates" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname.includes('/templates') ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname.includes('/templates') ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14h-2V9h-2V7h4v10z" />
            </svg>
            Templates
          </Link>
          
          {/* Campaigns */}
          <Link 
            to="/campaigns" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname.includes('/campaigns') ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname.includes('/campaigns') ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM10 4h4v2h-4V4zm6 14H8v-2h8v2zm2-4H6v-6h12v6z" />
            </svg>
            Campaigns
          </Link>
          
          {/* Analytics */}
          <Link 
            to="/analytics" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname === '/analytics' ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname === '/analytics' ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
            </svg>
            Analytics
          </Link>
          
          {/* Catalog */}
          <Link 
            to="/catalog" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname.includes('/catalog') ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname.includes('/catalog') ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
            </svg>
            Catalog
          </Link>
          
          {/* Orders */}
          <Link 
            to="/orders" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname === '/orders' ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname === '/orders' ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
            </svg>
            Orders
          </Link>
          
          {/* Phones */}
          <Link 
            to="/phones" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname === '/phones' ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname === '/phones' ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z" />
            </svg>
            Phones
          </Link>
          
          {/* Settings */}
          <Link 
            to="/settings" 
            className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${location.pathname === '/settings' ? 'text-whatsapp-dark-green bg-whatsapp-light-green' : 'text-gray-700 hover:bg-whatsapp-light-green hover:text-whatsapp-dark-green'}`}
          >
            <svg className={`mr-4 h-6 w-6 ${location.pathname === '/settings' ? 'text-whatsapp-dark-green' : 'text-gray-500 group-hover:text-whatsapp-dark-green'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
            </svg>
            Settings
          </Link>

          {/* Logout */}
          <button 
            onClick={handleLogout}
            className="group flex items-center px-2 py-2 text-base font-medium rounded-md w-full text-left text-gray-700 hover:bg-red-100 hover:text-red-700"
          >
            <svg className="mr-4 h-6 w-6 text-gray-500 group-hover:text-red-700" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
            </svg>
            Logout
          </button>
        </div>
      </nav>

      {/* WhatsApp Phone Selection */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-4">
        <div className="flex items-center">
          <svg className="h-5 w-5 text-whatsapp-green" fill="currentColor" viewBox="0 0 24 24">
            <path d="M16 12V6c0-2.21-1.79-4-4-4S8 3.79 8 6v6h8zm-4 8c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm8-9c0 3.14-1.28 5.59-3 7v1c0 1.1-.9 2-2 2H9c-1.1 0-2-.9-2-2v-1c-1.72-1.41-3-3.86-3-7 0-1.1.9-2 2-2s2 .9 2 2v4h2V6c0-2.21 1.79-4 4-4s4 1.79 4 4v7h2V9c0-1.1.9-2 2-2s2 .9 2 2z" />
          </svg>
          {isPhonesLoading ? (
            <div className="ml-2 w-full animate-pulse h-10 bg-gray-200 rounded"></div>
          ) : (
            <select className="ml-2 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-whatsapp-green focus:border-whatsapp-green sm:text-sm rounded-md">
              {phones && phones.length > 0 ? (
                phones.map((phone: any) => (
                  <option key={phone.id} value={phone.id}>{phone.phone_number}</option>
                ))
              ) : (
                <option value="">No WhatsApp numbers available</option>
              )}
              <option value="add_new">➕ Add New Phone...</option>
            </select>
          )}
        </div>
        <div className="mt-2 text-xs text-gray-500">
          <p>Active WhatsApp number for sending messages</p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
